import { useState } from 'react';
import type { ReactNode } from 'react';
import { AuthError } from '../auth/provider';
import type { AuthProvider } from '../auth/provider';

/**
 * Sign in.
 *
 * The dev banner is not decoration. `VITE_AUTH_FAKE` mints unsigned tokens and
 * the whole point of `core-api`'s `FakeVerifier` refusing to exist under
 * `NODE_ENV=production` is that the two modes must never be confusable. A screen
 * that looked identical either way would undo that on the other side of the
 * wire, so this one says which it is.
 */
export function SignIn({ auth }: { auth: AuthProvider }): ReactNode {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      await auth.signIn(email, password);
    } catch (e) {
      setError(
        e instanceof AuthError && e.reason === 'unavailable'
          ? 'Could not reach the sign-in service. Check the connection.'
          : 'That email and password did not match.',
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="signin">
      <form
        className="signin__panel"
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
      >
        <h1>yourstore</h1>
        {auth.kind === 'fake' ? (
          <p className="signin__dev">
            Development sign-in. Any email works and the token is unsigned — this build talks only
            to a <code>core-api</code> started with <code>AUTH_FAKE_TOKENS=1</code>.
          </p>
        ) : null}

        <label className="field">
          <span>Email</span>
          <input
            type="email"
            autoComplete="username"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </label>

        {auth.kind === 'firebase' ? (
          <label className="field">
            <span>Password</span>
            <input
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </label>
        ) : null}

        {error !== null ? <p className="error">{error}</p> : null}

        <button type="submit" className="button button--primary" disabled={busy}>
          {busy ? 'Signing in…' : 'Sign in'}
        </button>
      </form>
    </main>
  );
}
